import {Injectable} from '@angular/core';

/**
 * Service that converts XML to JSON following Newtonsoft.Json conventions
 * Replicates the behavior of JsonConvert.SerializeXmlNode from C#
 */
@Injectable({
  providedIn: 'root',
})
export class XmlToJsonConverterService {
  /**
   * Converts a stored view configuration into the Newtonsoft-shaped object the detail parser
   * expects. MIM stores RCDC XML; the Identity Director Service stores the JSON document that
   * `convert()` would produce (as a string, or already parsed). Returns `{error: true}` when
   * the input is neither.
   */
  convertConfiguration(configuration: any): any {
    if (configuration == null) {
      return {error: true};
    }
    if (typeof configuration === 'object') {
      return configuration;
    }
    if (typeof configuration !== 'string') {
      return {error: true};
    }
    const trimmed = configuration.trim();
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        return parsed != null && typeof parsed === 'object' ? parsed : {error: true};
      } catch (error) {
        return {error: true};
      }
    }
    return this.convert(configuration);
  }

  /**
   * Converts XML string to JSON object matching Newtonsoft.Json output format
   */
  convert(xmlString: string): any {
    try {
      if (!xmlString || xmlString.trim().length === 0) {
        return {error: true};
      }

      const document = this.parseXmlString(xmlString);
      const result: any = {};

      // Process document element (root)
      if (document.documentElement) {
        const rootName = document.documentElement.nodeName;
        result[rootName] = this.processElement(document.documentElement);
      }

      return result;
    } catch (error) {
      return {error: true};
    }
  }

  /**
   * Parses XML string into Document object with error handling
   */
  private parseXmlString(xmlString: string): Document {
    const parser = new DOMParser();
    const document = parser.parseFromString(xmlString, 'text/xml');

    // Check for parsing errors
    const parserError = document.querySelector('parsererror');
    if (parserError) {
      throw new Error('XML parsing failed');
    }

    return document;
  }

  /**
   * Processes an XML element and returns its JSON representation
   */
  private processElement(element: Element): any {
    const result: any = {};

    // Process attributes
    const attributes = this.extractAttributes(element);
    Object.assign(result, attributes);

    // Process child nodes
    const childData = this.processChildNodes(element);

    // Merge child data with attributes
    if (childData !== null) {
      if (typeof childData === 'object' && !Array.isArray(childData)) {
        Object.assign(result, childData);
      } else if (Object.keys(result).length === 0) {
        // If no attributes and child is primitive/array, return it directly
        return childData;
      } else {
        // Has attributes and primitive content
        if (typeof childData === 'string') {
          result['#text'] = childData;
        } else {
          Object.assign(result, childData);
        }
      }
    }

    return Object.keys(result).length > 0 ? result : {};
  }

  /**
   * Extracts attributes from element and returns them with @ prefix
   */
  private extractAttributes(element: Element): any {
    const attributes: any = {};

    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];
      attributes[`@${attr.name}`] = attr.value;
    }

    return attributes;
  }

  /**
   * Processes all child nodes of an element
   */
  private processChildNodes(element: Element): any {
    const children = Array.from(element.childNodes);

    if (children.length === 0) {
      return null;
    }

    // Check if only text/cdata content
    const nonTextNodes = children.filter(node => node.nodeType === Node.ELEMENT_NODE);

    if (nonTextNodes.length === 0) {
      // Only text/CDATA nodes
      return this.processTextContent(children);
    }

    // Process mixed or element-only content
    const childData: any = {};
    const elementGroups = new Map<string, any[]>();
    let hasText = false;
    let textContent = '';

    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const childElement = child as Element;
        const nodeName = childElement.nodeName;
        const childValue = this.processElement(childElement);

        if (!elementGroups.has(nodeName)) {
          elementGroups.set(nodeName, []);
        }
        elementGroups.get(nodeName)!.push(childValue);
      } else if (child.nodeType === Node.TEXT_NODE) {
        const text = this.normalizeText(child.textContent || '');
        if (text) {
          hasText = true;
          textContent += text;
        }
      } else if (child.nodeType === Node.CDATA_SECTION_NODE) {
        hasText = true;
        childData['#cdata-section'] = child.textContent;
      }
    }

    // Build result object
    elementGroups.forEach((values, name) => {
      if (values.length === 1) {
        childData[name] = values[0];
      } else {
        childData[name] = values;
      }
    });

    // Add text content if present with elements
    if (hasText && nonTextNodes.length > 0) {
      if (textContent) {
        childData['#text'] = textContent;
      }
    }

    return Object.keys(childData).length > 0 ? childData : hasText ? textContent : null;
  }

  /**
   * Processes text and CDATA content from nodes
   */
  private processTextContent(nodes: Node[]): string | any {
    let result = '';
    let hasCData = false;
    const cdataContent: any = {};

    for (const node of nodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = this.normalizeText(node.textContent || '');
        if (text) {
          result += text;
        }
      } else if (node.nodeType === Node.CDATA_SECTION_NODE) {
        hasCData = true;
        // If multiple CDATA sections, this will keep the last one
        // This matches Newtonsoft behavior
        cdataContent['#cdata-section'] = node.textContent;
      } else if (node.nodeType === Node.COMMENT_NODE) {
        // Ignore comments completely
        continue;
      }
    }

    if (hasCData && !result) {
      return cdataContent;
    } else if (hasCData && result) {
      cdataContent['#text'] = result;
      return cdataContent;
    }

    return result || null;
  }

  /**
   * Normalizes text content by trimming whitespace
   */
  private normalizeText(text: string): string {
    // Preserve single spaces but trim and collapse multiple spaces
    const normalized = text.replace(/\s+/g, ' ').trim();
    return normalized;
  }
}
